import { defineStore } from 'pinia'   
import { computed, ref, Ref } from 'vue'
import { AddressApi, DEBUG_WARN } from '@/util'
import { useUserStore } from './user'
import { ICountryDTO } from '@/types/country'
import { IDeliveryAddressDTO } from '@/types/shoppingCart'

/** store of all countries + default delivery addresses of the current partner */
export const useAddressStore = defineStore('addressStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _countries: Ref<ICountryDTO[] | null> = ref(null)
    const _deliveryAddresses: Ref<IDeliveryAddressDTO[] | null> = ref(null)

    const state = { _countries, _deliveryAddresses }

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    /** currently active language */
    const countries = computed(() => _countries.value)
    const deliveryAddresses = computed(() => _deliveryAddresses.value)

    const getters = { countries, deliveryAddresses }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    function loadCountries() {
        _countries.value = null

        const api = new AddressApi
        api.getCountries()
            .then(response => _countries.value = response.data)
            .catch(error => DEBUG_WARN('AddressStore: Failed to load countries:', error))
    }

    function loadDeliveryAddresses(username?: string) {
        resetDeliveryAddresses()

        if (username) {
            if (username == 'cro@crosendal.dk') {
                loadTestDeliveryAddresses()
            }
            else {
                const api = new AddressApi
                api.getDeliveryAddresses()
                    .then(response => _deliveryAddresses.value = response.data)
                    .catch(error => DEBUG_WARN('AddressStore: Failed to load delivery addresses:', error))
            }
        }
    }

    function loadTestDeliveryAddresses() {
        _deliveryAddresses.value = [
            {
                name1: 'XXX Consulting ApS',
                name2: 'c/o Hans Hansen',
                streetName: 'Skolebakken',
                houseNumber: '5B',
                zipCode: '6000',
                cityName: 'Kolding',
                countryCode: 'DK',
                attention: ''
            },
            {
                name1: 'Hans Hansen',
                name2: '',
                streetName: 'Bakkesvinget',
                houseNumber: '20',
                zipCode: '4320',
                cityName: 'Krusaa',
                countryCode: 'DK',
                attention: ''
            },
            {
                name1: 'Ole Olsen',
                name2: '',
                streetName: 'Landskronagade',
                houseNumber: '2',
                zipCode: '2100',
                cityName: 'Koebenhavn OE',
                countryCode: 'DK',
                attention: ''
            },
            {
                name1: 'Gunther Hermann',
                name2: '',
                streetName: 'Schleswigerstr.',
                houseNumber: '7',
                zipCode: '29455',
                cityName: 'Flensburg',
                countryCode: 'DE',
                attention: ''
            }
        ]
    }

    function resetDeliveryAddresses() {
        _deliveryAddresses.value = null
    }

    function getCountryNames(languageCode: string): { countryCode: string, name: string }[] {
        if (_countries.value == null) {
            return []
        }

        const result: { countryCode: string, name: string }[] = []

        for (const country of _countries.value) {
            const translatedName = country.names[languageCode].name
            result.push({
                countryCode: country.countryCode,
                name: translatedName
            })
        }

        return result
    }

    function getCountryNamesDict(languageCode: string): { [countryCode: string]: string }  {
        if (_countries.value == null) {
            return {}
        }

        const result: { [countryCode: string]: string } = {};

        for (const country of _countries.value) {
            const translatedName = country.names[languageCode].name;
            result[country.countryCode] = translatedName
        }                

        return result
    }

    function getNationalities(languageCode: string): { countryCode: string, nationality: string }[] {
        if (_countries.value == null) {
            return []
        }

        const result: { countryCode: string, nationality: string }[] = []

        for (const country of _countries.value) {
            const translatedNationality = country.names[languageCode].nationality
            result.push({
                countryCode: country.countryCode,
                nationality: translatedNationality
            })
        }

        return result
    }

    function getNationalitiesDict(languageCode: string): { [countryCode: string]: string } {
        if (_countries.value == null) {
            return {}
        }

        const result: { [countryCode: string]: string } = {}

        for (const country of _countries.value) {
            const translatedNationality = country.names[languageCode].nationality
            result[country.countryCode] = translatedNationality
        }

        return result
    }

    const actions = {
        loadCountries,
        loadDeliveryAddresses,
        resetDeliveryAddresses,
        getCountryNames,
        getCountryNamesDict,
        getNationalities,
        getNationalitiesDict
    }

    /********************************************************************************************************************
     INITIALIZATION
     ********************************************************************************************************************/
    // Ensure initial refresh
    if (_countries.value == null) {
        loadCountries()
    }

    // Setup dependency on user store
    const userStore = useUserStore()

    userStore.$subscribe((mutation, state) => {
        loadDeliveryAddresses(state._userData?.currentUser)
    })

    if (_deliveryAddresses.value == null) {
        loadDeliveryAddresses(userStore.userData?.currentUser)
    }

    /********************************************************************************************************************
     INTERFACE
     ********************************************************************************************************************/
    return { ...state, ...getters, ...actions }
})